import { useEffect, useState, useSyncExternalStore } from "react";
import { Alert, Button, CircularProgress, Paper, Snackbar, Typography } from "@mui/material";
import { settingsStore } from "../../settings/store";
import { saveDeviceSettings, saveUserSettings } from "../../settings/persistence";

export default function SettingsSaveBar() {
  const { dirty, keys } = useSettingsDirty();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (settingsStore.isDirty()) e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveUserSettings(settingsStore.getAllValues("user"));
      saveDeviceSettings(settingsStore.getAllValues("device"));
      settingsStore.markSaved();
      setToast({ type: "success", msg: "설정이 저장되었습니다" });
    } catch (e) {
      setToast({ type: "error", msg: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {dirty && (
        <Paper
          elevation={3}
          sx={{
            position: "sticky",
            bottom: 16,
            mt: 2,
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderRadius: 2,
          }}
        >
          <Typography fontSize={13} color="text.secondary" sx={{ flex: 1 }}>
            {keys.length}개 항목이 변경되었습니다
          </Typography>
          <Button size="small" onClick={() => settingsStore.discard()} disabled={saving}>
            되돌리기
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {saving ? "저장 중" : "저장"}
          </Button>
        </Paper>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity={toast?.type ?? "success"} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

export function useSettingsDirty() {
  const dirty = useSyncExternalStore(settingsStore.subscribeMeta, settingsStore.isDirty);
  const keys = useSyncExternalStore(settingsStore.subscribeMeta, settingsStore.getDirtyKeys);
  return { dirty, keys };
}
